import { useEffect, useState } from "react";
import API from "../../../lib/api";
import { Button } from "../../ui/button";
import { Pill, fmtDT } from "../hsUtils";
import { TONE_CLASSES } from "../../assets/assetUtils";
import { KindPill, err } from "./libraryUtils";
import { Megaphone, CaretDown, CaretUp, Eye, CheckCircle } from "@phosphor-icons/react";

const SEEN_KEY = "hsLibraryWhatsNewSeen";

/** "What's new in the library" — recent publishes and new versions, with a count of entries since this browser last looked. */
export function WhatsNewFeed({ onPreview }) {
  const [entries, setEntries] = useState(null);
  const [open, setOpen] = useState(false);
  const [limit, setLimit] = useState(8);
  const [seenAt] = useState(() => localStorage.getItem(SEEN_KEY) || "");

  useEffect(() => {
    API.get("/hs/library/whats-new", { params: { limit: 50 } }).then((r) => {
      const list = r.data.entries || [];
      setEntries(list);
      const unseen = list.filter((e) => !seenAt || e.published_at > seenAt).length;
      setOpen(unseen > 0);
    }).catch((e) => { err(e, "Couldn't load what's new"); setEntries([]); });
  }, [seenAt]);

  useEffect(() => { if (open && entries?.length) localStorage.setItem(SEEN_KEY, entries[0].published_at); }, [open, entries]);

  if (!entries?.length) return null;
  const unseen = entries.filter((e) => !seenAt || e.published_at > seenAt).length;
  const shown = entries.slice(0, limit);
  return (
    <section className="rounded-lg border border-border bg-card" data-testid="hs-library-whats-new">
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full flex items-center gap-2 px-4 py-3 text-left" data-testid="hs-library-whats-new-toggle">
        <Megaphone size={16} className="text-primary" weight="fill" />
        <span className="text-sm font-semibold">What's new in the library</span>
        {unseen > 0 && <Pill tone={TONE_CLASSES.soon} testid="hs-library-whats-new-unseen">{unseen} since your last look</Pill>}
        <span className="ml-auto text-xs text-muted-foreground inline-flex items-center gap-1">{entries.length} recent {open ? <CaretUp size={12} /> : <CaretDown size={12} />}</span>
      </button>
      {open && (
        <ul className="divide-y divide-border/60 border-t border-border/60" data-testid="hs-library-whats-new-list">
          {shown.map((e) => {
            const fresh = !seenAt || e.published_at > seenAt;
            return (
              <li key={`${e.item_id}-${e.version}`} className="px-4 py-2.5 flex flex-wrap items-center gap-2" data-testid={`hs-library-whats-new-${e.item_id}-${e.version}`}>
                <span className={`h-2 w-2 rounded-full shrink-0 ${fresh ? "bg-primary" : "bg-transparent"}`} aria-hidden="true" />
                <KindPill kind={e.kind} />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate">{e.title}{e.trade ? <span className="text-muted-foreground font-normal"> · {e.trade}</span> : null}</p>
                  <p className="text-xs text-muted-foreground truncate">{e.change === "new" ? "New in the library" : `Updated to v${e.version}`}{e.notes ? ` — ${e.notes}` : e.change === "new" && e.summary ? ` — ${e.summary}` : ""} · {fmtDT(e.published_at)}</p>
                </div>
                <Pill tone={e.change === "new" ? TONE_CLASSES.ok : TONE_CLASSES.soon} testid={`hs-library-whats-new-change-${e.item_id}-${e.version}`}>{e.change === "new" ? "New" : `v${e.version}`}</Pill>
                {e.my_copies > 0 && <Pill tone={TONE_CLASSES.none} testid={`hs-library-whats-new-copies-${e.item_id}-${e.version}`}><CheckCircle size={11} className="mr-1" /> In your workspace</Pill>}
                <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => onPreview(e.item_id)} data-testid={`hs-library-whats-new-preview-${e.item_id}-${e.version}`}><Eye size={12} className="mr-1" /> Preview</Button>
              </li>
            );
          })}
          {entries.length > limit && <li className="px-4 py-2"><Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setLimit((l) => l + 10)} data-testid="hs-library-whats-new-more">Show older</Button></li>}
        </ul>
      )}
    </section>
  );
}
